import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Loader2, CheckCircle2, RotateCcw, Send, Mail, Inbox, MessageSquareReply, Link2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const when = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "—");
const FILTERS = [["open", "Open"], ["resolved", "Resolved"], ["all", "All"]];

function ReplyDialog({ r, templates, open, setOpen, onSent }) {
  const [tpl, setTpl] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const chosen = templates.find((t) => t.id === tpl);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/reports/${r.id}/reply`, { template: tpl, note });
      toast.success(`“${data.label}” sent to ${data.email}${data.resolved ? " · report resolved" : ""}`);
      setOpen(false); setTpl(""); setNote(""); onSent();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't send"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) { setOpen(v); if (!v) { setTpl(""); setNote(""); } } }}>
      <DialogContent data-testid={`reply-dialog-${r.id}`} className="bg-[#121212] border-[#2a2a2a] text-[#f3f4f6] max-w-lg max-h-[90dvh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">Reply to #{r.id}</DialogTitle></DialogHeader>
        <DialogDescription className="text-sm text-[#9ca3af]">To {r.email} · {r.track_title}. Replies from the buyer come to your inbox.</DialogDescription>
        <div className="space-y-2">
          {templates.map((t) => (
            <button key={t.id} data-testid={`reply-template-${t.id}`} onClick={() => setTpl(t.id)}
              className={`w-full text-left rounded-xl border p-3 transition-colors ${tpl === t.id ? "border-[#e2ff31] bg-[#e2ff31]/5" : "border-[#2a2a2a] hover:border-[#4b5563]"}`}>
              <p className="text-sm font-semibold flex flex-wrap items-center gap-2">{t.label}
                {t.links && <span className="text-[10px] font-mono text-[#e2ff31] inline-flex items-center gap-1"><Link2 className="w-3 h-3" />fresh links</span>}
                <span className={`text-[10px] font-mono ${t.resolve ? "text-[#22c55e]" : "text-[#f59e0b]"}`}>{t.resolve ? "resolves" : "keeps open"}</span>
              </p>
              <p className="text-xs text-[#9ca3af] mt-1 line-clamp-2">{t.preview.replace(/\{title\}/g, r.track_title)}</p>
            </button>
          ))}
        </div>
        <textarea data-testid={`reply-note-${r.id}`} value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={2000}
          placeholder="Optional personal note (added under the message)"
          className="w-full rounded-lg bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#e2ff31] outline-none p-3 text-sm resize-none" />
        <button data-testid={`reply-send-${r.id}`} onClick={send} disabled={!tpl || busy}
          className="w-full flex items-center justify-center gap-2 rounded-full bg-[#e2ff31] text-black hover:bg-[#c8e619] font-semibold py-3 transition-colors disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {chosen ? `Send${chosen.resolve ? " & resolve" : ""}` : "Pick a reply"}
        </button>
      </DialogContent>
    </Dialog>
  );
}

function ReportCard({ r, onChanged, templates }) {
  const [replyOpen, setReplyOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const act = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "resend") {
        const { data } = await api.post(`/admin/reports/${r.id}/resend`);
        toast.success(`Fresh links sent to ${data.email}`);
      } else {
        await api.patch(`/admin/reports/${r.id}`, { status: kind });
        toast.success(kind === "resolved" ? `Report #${r.id} resolved` : `Report #${r.id} reopened`);
      }
      onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Something went wrong"); }
    finally { setBusy(null); }
  };
  const open = r.status === "open";
  return (
    <div data-testid={`report-card-${r.id}`} className={`rounded-xl border p-4 ${open ? "border-[#f59e0b]/40 bg-[#f59e0b]/[0.03]" : "border-[#2a2a2a] bg-[#0f0f0f]"}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs text-[#6b7280]">#{r.id}</span>
            <span data-testid={`report-issue-label-${r.id}`} className={`text-xs font-semibold rounded-full px-2 py-0.5 border ${open ? "text-[#f59e0b] border-[#f59e0b]/40" : "text-[#22c55e] border-[#22c55e]/40"}`}>{r.issue_label}</span>
            <span className="truncate">{r.track_title}</span>
          </p>
          <p className="text-xs text-[#9ca3af] mt-1">
            <a href={`mailto:${r.email}?subject=Re: report %23${r.id}`} className="hover:text-[#e2ff31] inline-flex items-center gap-1"><Mail className="w-3 h-3" />{r.email}</a>
            {" · "}reported {when(r.created_at)} · bought {when(r.purchased_at)} · {r.download_count} download{r.download_count === 1 ? "" : "s"}
          </p>
        </div>
        <span data-testid={`report-status-${r.id}`} className={`text-[10px] font-mono uppercase tracking-widest ${open ? "text-[#f59e0b]" : "text-[#22c55e]"}`}>
          {open ? "Open" : `Resolved ${when(r.resolved_at)}`}
        </span>
      </div>
      {r.message && <p data-testid={`report-message-${r.id}`} className="text-sm text-[#d1d5db] mt-3 whitespace-pre-wrap break-words">{r.message}</p>}
      {r.screenshots.length > 0 && (
        <div className="flex gap-2 mt-3">
          {r.screenshots.map((u, i) => (
            <a key={i} href={u} target="_blank" rel="noreferrer" data-testid={`report-shot-${r.id}-${i}`}
              className="w-16 h-16 rounded-lg overflow-hidden border border-[#2a2a2a] hover:border-[#e2ff31] transition-colors">
              <img src={u} alt={`Screenshot ${i + 1}`} className="w-full h-full object-cover" />
            </a>
          ))}
        </div>
      )}
      {r.replies?.length > 0 && (
        <p data-testid={`report-last-reply-${r.id}`} className="text-xs text-[#9ca3af] mt-3 flex items-center gap-1.5">
          <MessageSquareReply className="w-3.5 h-3.5 text-[#e2ff31]" />
          Replied “{r.replies[r.replies.length - 1].label}” · {when(r.replies[r.replies.length - 1].at)}
          {r.replies.length > 1 && <span className="text-[#6b7280]">(+{r.replies.length - 1} earlier)</span>}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2 mt-4">
        <button data-testid={`report-reply-${r.id}`} onClick={() => setReplyOpen(true)} disabled={!!busy || !templates.length}
          className="inline-flex items-center gap-1.5 rounded-full bg-[#e2ff31] text-black hover:bg-[#c8e619] font-semibold text-xs px-3.5 py-2 transition-colors disabled:opacity-60">
          <MessageSquareReply className="w-3.5 h-3.5" /> Reply
        </button>
        <button data-testid={`report-resend-${r.id}`} onClick={() => act("resend")} disabled={!!busy}
          className="inline-flex items-center gap-1.5 rounded-full border border-[#e2ff31]/50 text-[#e2ff31] hover:bg-[#e2ff31]/10 font-semibold text-xs px-3.5 py-2 transition-colors disabled:opacity-60">
          {busy === "resend" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Re-send files
        </button>
        {open ? (
          <button data-testid={`report-resolve-${r.id}`} onClick={() => act("resolved")} disabled={!!busy}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#22c55e]/50 text-[#22c55e] hover:bg-[#22c55e]/10 font-semibold text-xs px-3.5 py-2 transition-colors">
            {busy === "resolved" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Mark resolved
          </button>
        ) : (
          <button data-testid={`report-reopen-${r.id}`} onClick={() => act("open")} disabled={!!busy}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#2a2a2a] text-[#9ca3af] hover:text-white font-semibold text-xs px-3.5 py-2 transition-colors">
            {busy === "open" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Reopen
          </button>
        )}
        {r.resend_count > 0 && (
          <span data-testid={`report-resent-${r.id}`} className="text-xs text-[#6b7280]">Files re-sent {r.resend_count}× · last {when(r.last_resent_at)}</span>
        )}
      </div>
      <ReplyDialog r={r} templates={templates} open={replyOpen} setOpen={setReplyOpen} onSent={onChanged} />
    </div>
  );
}

export const ProblemReports = ({ onCounts }) => {
  const [filter, setFilter] = useState("open");
  const [data, setData] = useState(null);
  const [templates, setTemplates] = useState([]);
  useEffect(() => { api.get("/admin/report-templates").then((r) => setTemplates(r.data)).catch(() => {}); }, []);
  const load = useCallback(() => api.get("/admin/reports", { params: { status: filter } })
    .then((r) => { setData(r.data); onCounts?.(r.data.counts); }).catch(() => setData({ reports: [], counts: {}, error: true })), [filter, onCounts]);
  useEffect(() => { setData(null); load(); }, [load]);

  return (
    <div data-testid="problem-reports" className="bg-[#121212] border border-[#2a2a2a] rounded-2xl p-6">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <h2 className="font-heading text-lg font-semibold flex items-center gap-2"><Inbox className="w-5 h-5 text-[#e2ff31]" /> Problem reports</h2>
          <p className="text-sm text-[#9ca3af]">File problems buyers sent from My Purchases. Re-send files emails them fresh 24h links.</p>
        </div>
        <div className="flex gap-2">
          {FILTERS.map(([v, l]) => (
            <button key={v} data-testid={`reports-filter-${v}`} onClick={() => setFilter(v)}
              className={`rounded-full px-3 py-1 text-xs font-semibold border transition-colors ${filter === v ? "bg-[#e2ff31] text-black border-[#e2ff31]" : "border-[#2a2a2a] text-[#9ca3af] hover:text-white"}`}>
              {l}{data?.counts && v !== "all" && data.counts[v] !== undefined ? ` (${data.counts[v]})` : ""}
            </button>
          ))}
        </div>
      </div>
      {!data ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#e2ff31]" /></div>
        : data.error ? <p data-testid="reports-error" className="text-sm text-[#ef4444] text-center py-8">Couldn't load reports. Refresh to try again.</p>
        : data.reports.length === 0 ? (
          <p data-testid="reports-empty" className="text-sm text-[#6b7280] text-center py-10">
            {filter === "open" ? "No open reports. Every buyer is happy." : "No reports here yet."}
          </p>
        ) : <div className="space-y-3">{data.reports.map((r) => <ReportCard key={r.id} r={r} onChanged={load} templates={templates} />)}</div>}
    </div>
  );
};
